import db from './index';
import { getErrorMsg, logWithRethrow, Operation } from './helpers';
import { wrapQuery } from './queryWrapper';
import { makeDefaultClear } from './defaultDaoFactories';
import { areIdentitiesEqual, type CurrentUserIdentity } from 'shared/domain/currentUser/currentUserIdentity';

export interface ConfigData {
  _id: number;
  locale: string;
  frontendVersion: string;
  api: string;
  projectId: string;
  organizationId?: string;
  /** Project timezone for date calculations (analytics). */
  timezone?: string;
  /**
   * Persisted bearer token expiration (ms epoch). Kept here so a freshly
   * restarted Service Worker can skip a needless main-thread token refresh
   * round-trip when the previously issued token is still valid.
   * The bearer token itself is intentionally NOT persisted.
   */
  tokenExpiration?: number;
  /** The last logged-in user, for the logs of a restarted worker. Written by the worker. */
  currentUser?: CurrentUserIdentity;
}

export const clear = makeDefaultClear(db, db.config);

export const get = wrapQuery(
  db,
  (): Promise<ConfigData | undefined> =>
    db.config
      .toCollection()
      .last()
      .catch((e) =>
        logWithRethrow({
          msg: getErrorMsg(Operation.get, 'config'),
          errorObj: e,
          additionalInfo: null,
        })
      )
);

export const getProjectId = async (): Promise<string | undefined> => (await get())?.projectId;

/**
 * Fields the worker writes itself, not the page: a config the page writes
 * again (the whole row) keeps them.
 */
const WORKER_OWNED_FIELDS = ['tokenExpiration', 'currentUser'] as const;

function keepWorkerOwnedFields(
  previous: ConfigData | undefined,
  data: Omit<ConfigData, '_id'>
): Omit<ConfigData, '_id'> {
  return WORKER_OWNED_FIELDS.reduce(
    (merged, field) =>
      previous?.[field] !== undefined && merged[field] === undefined ? { ...merged, [field]: previous[field] } : merged,
    data
  );
}

export const add = wrapQuery(db, async (data: Omit<ConfigData, '_id'>): Promise<void> => {
  await db.transaction('rw', db.config, async function configAddTransaction() {
    const previous = await db.config.toCollection().last().catch(() => undefined);
    const merged = keepWorkerOwnedFields(previous, data);
    await clear();
    return db.config.put(merged).catch((e) =>
      logWithRethrow({
        msg: getErrorMsg(Operation.add, 'config'),
        errorObj: e,
        additionalInfo: { query: { data } },
      })
    );
  });
});

/**
 * Changes one field of the stored config without rewriting the whole row.
 * Best-effort: no-ops when no config row exists yet.
 */
function makeStoredConfigUpdate<TArgs extends unknown[]>(
  fieldLabel: string,
  change: (existing: ConfigData, ...args: TArgs) => ConfigData | undefined
): (...args: TArgs) => Promise<void> {
  return wrapQuery(db, async (...args: TArgs): Promise<void> => {
    await db
      .transaction('rw', db.config, async function updateStoredConfigTransaction() {
        const existing = await db.config.toCollection().last();
        const changed = existing && change(existing, ...args);
        if (changed) {
          await db.config.put(changed);
        }
      })
      .catch((e) =>
        logWithRethrow({
          msg: `${getErrorMsg(Operation.put, 'config')} (${fieldLabel})`,
          errorObj: e,
          additionalInfo: { query: { args } },
        })
      );
  });
}

/** Persist only the bearer token expiration. */
export const setTokenExpiration = makeStoredConfigUpdate(
  'tokenExpiration',
  (existing, tokenExpiration: number) => ({ ...existing, tokenExpiration })
);

/** Clear persisted bearer token expiration (e.g. on logout). */
export const clearTokenExpiration = makeStoredConfigUpdate('clearTokenExpiration', (existing) => {
  if (!existing.tokenExpiration) {
    return undefined;
  }
  const { tokenExpiration: _omit, ...rest } = existing;
  return rest;
});

/**
 * The last logged-in user, so the logs of a restarted worker belong to them
 * before the page announces anyone (PT-7276).
 */
export const setCurrentUser = makeStoredConfigUpdate(
  'currentUser',
  (existing, currentUser: CurrentUserIdentity) =>
    existing.currentUser && areIdentitiesEqual(existing.currentUser, currentUser)
      ? undefined
      : { ...existing, currentUser }
);

/** Forgets the last logged-in user (logout). */
export const clearCurrentUser = makeStoredConfigUpdate('clearCurrentUser', (existing) => {
  if (!existing.currentUser) {
    return undefined;
  }
  const { currentUser: _omit, ...rest } = existing;
  return rest;
});
